import { useEffect, useRef, useState } from "react";

export default function App() {
  const canvasRef = useRef(null);
  const [contactVisible, setContactVisible] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let W, H, animId, start = null, done = false;
    let COLS, ROWS, centerCol, centerRow, tileStates, allTiles;

    const TILE_SIZE = 220;
    const GAP = 6;

    function buildGrid() {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;

      COLS = Math.ceil(W / TILE_SIZE);
      if (COLS % 2 === 0) COLS += 1;
      ROWS = Math.ceil(H / TILE_SIZE);
      if (ROWS % 2 === 0) ROWS += 1;

      centerCol = Math.floor(COLS / 2);
      centerRow = Math.floor(ROWS / 2);

      allTiles = [];
      for (let r = 0; r < ROWS; r++)
        for (let c = 0; c < COLS; c++)
          if (!(c === centerCol && r === centerRow))
            allTiles.push({ col: c, row: r });

      const prev = tileStates || {};
      tileStates = {};
      for (const t of allTiles) {
        const key = `${t.col},${t.row}`;
        tileStates[key] = prev[key] || {
          col: t.col, row: t.row,
          alpha: 0, state: "idle",
          nextFire: Math.random() * 2.5,
          holdDur: 0, shape: "square",
        };
      }
    }

    buildGrid();
    window.addEventListener("resize", buildGrid);

    function getOffset() {
      return {
        ox: (W - TILE_SIZE * COLS) / 2,
        oy: (H - TILE_SIZE * ROWS) / 2,
      };
    }

    const easeOut   = t => 1 - Math.pow(1 - t, 3);
    const easeInOut = t => t < 0.5 ? 2*t*t : -1+(4-2*t)*t;
    const clamp     = (v, a, b) => Math.max(a, Math.min(b, v));
    const pt        = (p, s, e) => clamp((p - s) / (e - s), 0, 1);
    const rnd       = (a, b) => Math.random() * (b - a) + a;
    const pick      = arr => arr[Math.floor(Math.random() * arr.length)];

    const SHAPE_TYPES = [
      "square", "rect-h", "rect-v",
      "tri-tl", "tri-tr", "tri-bl", "tri-br",
      "rect-h-top", "rect-h-bot", "rect-v-left", "rect-v-right",
    ];

    function drawShape(type, x, y, w, alpha, scale) {
      const cx = x + w / 2, cy = y + w / 2;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#ffffff";
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);
      switch (type) {
        case "square":       ctx.fillRect(-w/2,-w/2,w,w); break;
        case "rect-h":       ctx.fillRect(-w/2,-w/4,w,w/2); break;
        case "rect-v":       ctx.fillRect(-w/4,-w/2,w/2,w); break;
        case "tri-tl":
          ctx.beginPath(); ctx.moveTo(-w/2,-w/2); ctx.lineTo(w/2,-w/2); ctx.lineTo(-w/2,w/2); ctx.closePath(); ctx.fill(); break;
        case "tri-tr":
          ctx.beginPath(); ctx.moveTo(-w/2,-w/2); ctx.lineTo(w/2,-w/2); ctx.lineTo(w/2,w/2); ctx.closePath(); ctx.fill(); break;
        case "tri-bl":
          ctx.beginPath(); ctx.moveTo(-w/2,-w/2); ctx.lineTo(-w/2,w/2); ctx.lineTo(w/2,w/2); ctx.closePath(); ctx.fill(); break;
        case "tri-br":
          ctx.beginPath(); ctx.moveTo(w/2,-w/2); ctx.lineTo(-w/2,w/2); ctx.lineTo(w/2,w/2); ctx.closePath(); ctx.fill(); break;
        case "rect-h-top":   ctx.fillRect(-w/2,-w/2,w,w*0.42); break;
        case "rect-h-bot":   ctx.fillRect(-w/2,w/2-w*0.42,w,w*0.42); break;
        case "rect-v-left":  ctx.fillRect(-w/2,-w/2,w*0.42,w); break;
        case "rect-v-right": ctx.fillRect(w/2-w*0.42,-w/2,w*0.42,w); break;
        default: ctx.fillRect(-w/2,-w/2,w,w);
      }
      ctx.restore();
    }

    const TOTAL      = 5.8;
    const MOSAIC_END = 4.6;
    const LOGO_START = 4.8;
    const POP_IN     = 0.12;
    const POP_OUT    = 0.15;

    function countVisible() {
      return Object.values(tileStates).filter(t => t.state === "in" || t.state === "hold").length;
    }

    function updateTiles(now) {
      const maxV = Math.max(4, Math.floor(COLS * ROWS * 0.08));
      for (const key in tileStates) {
        const t = tileStates[key];
        if (t.state === "idle") {
          if (now >= t.nextFire && now < MOSAIC_END && countVisible() < maxV) {
            t.state = "in"; t.stateStart = now;
            t.holdDur = rnd(0.15, 0.65);
            t.shape = pick(SHAPE_TYPES);
          }
        } else if (t.state === "in") {
          t.alpha = easeOut(pt(now, t.stateStart, t.stateStart + POP_IN));
          if (now >= t.stateStart + POP_IN) { t.alpha = 1; t.state = "hold"; t.stateStart = now; }
        } else if (t.state === "hold") {
          t.alpha = 1;
          if (now >= t.stateStart + t.holdDur) { t.state = "out"; t.stateStart = now; }
        } else if (t.state === "out") {
          t.alpha = 1 - easeInOut(pt(now, t.stateStart, t.stateStart + POP_OUT));
          if (now >= t.stateStart + POP_OUT) {
            t.alpha = 0; t.state = "idle";
            t.nextFire = now + rnd(0.12, 1.6);
          }
        }
      }
    }

    function drawTiles(ox, oy) {
      for (const key in tileStates) {
        const t = tileStates[key];
        if (t.alpha <= 0) continue;
        const x  = ox + t.col * TILE_SIZE + GAP / 2;
        const y  = oy + t.row * TILE_SIZE + GAP / 2;
        const w  = TILE_SIZE - GAP;
        const sc = 0.55 + t.alpha * 0.45;
        drawShape(t.shape, x, y, w, t.alpha, sc);
      }
    }

    function drawCenterTile(now, ox, oy) {
      if (now < LOGO_START) return;
      const p  = pt(now, LOGO_START, TOTAL);
      const w  = TILE_SIZE - GAP;
      const x  = ox + centerCol * TILE_SIZE + GAP / 2;
      const y  = oy + centerRow * TILE_SIZE + GAP / 2;
      const cx = x + w / 2, cy = y + w / 2;

      const popP  = easeOut(clamp(pt(p, 0.0, 0.18), 0, 1));
      const fadeP = easeInOut(clamp(pt(p, 0.16, 0.48), 0, 1));
      const sc    = 0.55 + popP * 0.45;
      const sqA   = popP * (1 - fadeP);

      if (sqA > 0.005) {
        ctx.save();
        ctx.globalAlpha = sqA;
        ctx.fillStyle = "#ffffff";
        ctx.translate(cx, cy); ctx.scale(sc, sc);
        ctx.fillRect(-w/2, -w/2, w, w);
        ctx.restore();
      }

      const logoProg = easeOut(clamp(pt(p, 0.16, 1.0), 0, 1));
      if (logoProg > 0) drawLogo(cx, cy, w, logoProg);
    }

    function drawLogo(cx, cy, S, progress) {
      const p = progress;
      const x0 = cx - S/2, y0 = cy - S/2;
      ctx.save();

      // Frame
      const fp = easeOut(clamp(pt(p, 0.0, 0.48), 0, 1));
      if (fp > 0) {
        const perim = S*4, drawn = perim*fp;
        ctx.strokeStyle = `rgba(30,74,36,${easeInOut(p)*0.95})`;
        ctx.lineWidth = Math.max(1, S*0.003);
        const segs = [[x0,y0,x0+S,y0],[x0+S,y0,x0+S,y0+S],[x0+S,y0+S,x0,y0+S],[x0,y0+S,x0,y0]];
        let rem = drawn;
        ctx.beginPath(); ctx.moveTo(x0, y0);
        for (const [ax,ay,bx,by] of segs) {
          const sl = Math.hypot(bx-ax,by-ay);
          if (rem<=0) break;
          if (rem>=sl) { ctx.lineTo(bx,by); rem-=sl; }
          else { const t=rem/sl; ctx.lineTo(ax+(bx-ax)*t,ay+(by-ay)*t); rem=0; }
        }
        ctx.stroke();
      }

      // Corners
      const cp = easeOut(clamp(pt(p, 0.04, 0.48), 0, 1));
      const cl = S * 0.09 * cp;
      if (cl > 0) {
        ctx.strokeStyle = `rgba(30,74,36,${easeInOut(p)})`;
        ctx.lineWidth = Math.max(1.5, S*0.005);
        const corners = [
          [[x0,y0+cl,x0,y0],[x0,y0,x0+cl,y0]],
          [[x0+S-cl,y0,x0+S,y0],[x0+S,y0,x0+S,y0+cl]],
          [[x0,y0+S-cl,x0,y0+S],[x0,y0+S,x0+cl,y0+S]],
          [[x0+S-cl,y0+S,x0+S,y0+S],[x0+S,y0+S,x0+S,y0+S-cl]],
        ];
        for (const [s1,s2] of corners) {
          ctx.beginPath();
          ctx.moveTo(s1[0],s1[1]); ctx.lineTo(s1[2],s1[3]);
          ctx.moveTo(s2[0],s2[1]); ctx.lineTo(s2[2],s2[3]);
          ctx.stroke();
        }
      }

      // SF — larger: 0.52 of tile size, shifted up slightly to rebalance
      const sfP = easeInOut(clamp(pt(p, 0.22, 0.76), 0, 1));
      if (sfP > 0) {
        ctx.font = `600 ${S * 0.52}px 'Palatino Linotype',Palatino,'Book Antiqua',Georgia,serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#ffffff";
        ctx.globalAlpha = sfP;
        ctx.fillText("SF", cx, cy - S * 0.03);
      }

      // Divider
      const dp = easeInOut(clamp(pt(p, 0.42, 0.78), 0, 1));
      if (dp > 0) {
        const hw = S * 0.36 * dp;
        ctx.globalAlpha = dp * 0.6;
        ctx.strokeStyle = "rgba(30,74,36,0.9)";
        ctx.lineWidth = Math.max(0.5, S*0.0015);
        ctx.beginPath();
        ctx.moveTo(cx-hw, cy+S*0.33); ctx.lineTo(cx+hw, cy+S*0.33);
        ctx.stroke();
      }

      // RESEARCH
      const rp = easeInOut(clamp(pt(p, 0.54, 1.0), 0, 1));
      if (rp > 0) {
        const fs = clamp(S * 0.054, 10, 22);
        ctx.font = `300 ${fs}px Georgia,serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#a8c4ac";
        ctx.globalAlpha = rp;
        ctx.fillText("R E S E A R C H", cx, cy + S * 0.41);
      }

      // Ornament
      const op = easeInOut(clamp(pt(p, 0.68, 1.0), 0, 1));
      if (op > 0) {
        const oy2 = cy + S*0.475, ll = S*0.07*op;
        ctx.globalAlpha = op * 0.6;
        ctx.fillStyle = "#1e4a24";
        ctx.beginPath(); ctx.arc(cx, oy2, Math.max(1.2, S*0.005), 0, Math.PI*2); ctx.fill();
        ctx.strokeStyle = "#1e4a24";
        ctx.lineWidth = Math.max(0.4, S*0.0012);
        ctx.beginPath();
        ctx.moveTo(cx-S*0.055-ll, oy2); ctx.lineTo(cx-S*0.055, oy2);
        ctx.moveTo(cx+S*0.055, oy2);   ctx.lineTo(cx+S*0.055+ll, oy2);
        ctx.stroke();
      }

      ctx.restore();
    }

    function renderFinal() {
      ctx.clearRect(0, 0, W, H);
      const { ox, oy } = getOffset();
      const cx = ox + centerCol * TILE_SIZE + TILE_SIZE / 2;
      const cy = oy + centerRow * TILE_SIZE + TILE_SIZE / 2;
      drawLogo(cx, cy, TILE_SIZE - GAP, 1);
    }

    function frame(ts) {
      if (done) return;
      if (!start) start = ts;
      const elapsed = (ts - start) / 1000;
      const prog = Math.min(elapsed / TOTAL, 1);

      ctx.clearRect(0, 0, W, H);
      const { ox, oy } = getOffset();

      updateTiles(elapsed);
      drawTiles(ox, oy);
      drawCenterTile(elapsed, ox, oy);

      if (prog >= 1) {
        done = true;
        renderFinal();
        setContactVisible(true);
        function keepAlive() { renderFinal(); animId = requestAnimationFrame(keepAlive); }
        animId = requestAnimationFrame(keepAlive);
        return;
      }
      animId = requestAnimationFrame(frame);
    }

    animId = requestAnimationFrame(frame);
    return () => {
      done = true;
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", buildGrid);
    };
  }, []);

  return (
    <div style={{ width:"100%", height:"100vh", background:"#0a1c0e", position:"relative", overflow:"hidden" }}>
      <canvas ref={canvasRef} style={{ position:"absolute", inset:0, width:"100%", height:"100%", display:"block" }} />
      <a
        href="mailto:info@sf-research.ltd"
        style={{
          position: "absolute",
          bottom: 32,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 30,
          fontFamily: "Georgia, serif",
          fontSize: 12,
          letterSpacing: "0.38em",
          textTransform: "uppercase",
          color: "#c8deca",
          textDecoration: "none",
          opacity: contactVisible ? 1 : 0,
          transition: "opacity 0.8s ease, color 0.4s ease",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={e => e.currentTarget.style.color = "#ffffff"}
        onMouseLeave={e => e.currentTarget.style.color = "#c8deca"}
      >
        Contact
      </a>
    </div>
  );
}
